/**
 * Feature Flag Constants - Single Source of Truth
 *
 * This file provides TypeScript intelliSense, prevents typos, and includes default values.
 * Import default values from defaultFeatureFlags.ts to avoid duplication.
 *
 * Usage:
 * import { FEATURE_FLAGS } from '@/constants/featureFlags'
 * const isEnabled = useFeatureFlag(FEATURE_FLAGS.USER_PROPERTY_TEST)
 *
 * Adding New Flags:
 * 1. Add the constant and default value to DEFAULT_FEATURE_FLAGS in config/defaultFeatureFlags.ts
 * 2. Add the constant name here (auto-generated from defaults)
 * 3. Add to Firebase Remote Config
 * 4. Use immediately in components
 */

import { DEFAULT_FEATURE_FLAGS } from '@/config/defaultFeatureFlags'

// Generate FEATURE_FLAGS constants from DEFAULT_FEATURE_FLAGS keys
// This ensures single source of truth while maintaining TypeScript intellisense
export const FEATURE_FLAGS = Object.keys(DEFAULT_FEATURE_FLAGS).reduce(
  (acc, key) => {
    // Convert snake_case to SCREAMING_SNAKE_CASE for constants
    const constantName = key.replace(/^web_/, '').toUpperCase()
    acc[constantName as keyof typeof acc] = key
    return acc
  },
  {} as Record<string, string>
) as {
  readonly [K in keyof typeof DEFAULT_FEATURE_FLAGS as Uppercase<
    K extends `web_${infer Rest}` ? Rest : K
  >]: K
}

/**
 * Type-safe feature flag names
 * This ensures you can only use valid flag names with TypeScript
 */
export type FeatureFlagName = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS]

/**
 * Helper function to get all feature flag names as an array
 * Useful for bulk operations
 */
export const getAllFeatureFlagNames = (): FeatureFlagName[] => {
  return Object.values(FEATURE_FLAGS)
}

/**
 * Helper to check if a string is a valid feature flag name
 */
export const isValidFeatureFlagName = (
  name: string
): name is FeatureFlagName => {
  return Object.values(FEATURE_FLAGS).includes(name as FeatureFlagName)
}

/**
 * Get the default value for a feature flag
 * This connects to the single source of truth in defaultFeatureFlags.ts
 */
export const getFeatureFlagDefault = (
  flagName: FeatureFlagName
): boolean | string => {
  return DEFAULT_FEATURE_FLAGS[flagName as keyof typeof DEFAULT_FEATURE_FLAGS]
}

/**
 * Get all feature flag defaults
 * Re-export from defaultFeatureFlags.ts for convenience
 */
export { DEFAULT_FEATURE_FLAGS } from '@/config/defaultFeatureFlags'
